import { ApplicationRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'
import { ModalStockOperationCardComponent } from '../../components/modal-components/modal-stock-operation-card/modal-stock-operation-card.component'
import { ModalStatusComponent } from '../../components/modal-components/modal-status/modal-status.component'
import {
  Depot,
  File,
  Producer,
  Product,
  ProductDepot,
  ProductMove,
  ProductMovePack,
  Supplier,
  Vat,
} from 'depoto-core/src/entities'
import { Events } from 'depoto-core/src/utils'
import { CoreService, NotificationService } from '../../services'
import { SchemaUtil, getThumbUrl as imageGetThumbUrl } from '../../utils'
import { TranslateService } from '@ngx-translate/core'
import * as Sentry from '@sentry/angular-ivy'
import { CompanyUtilService } from '../../services/company-util.service'

declare let disableBarcodeScanner: boolean

@Component({
  selector: 'stock-transfer-page',
  templateUrl: 'stock-transfer-page.component.html',
  styleUrls: ['stock-transfer-page.component.scss'],
})
export class StockTransferPageComponent implements OnInit, OnDestroy {
  @ViewChild('modalStockOperation') modalStockOperation: ModalStockOperationCardComponent
  @ViewChild('modalStockOperationProductCreate') modalStockOperationProductCreate: ModalStockOperationCardComponent
  @ViewChild(ModalStatusComponent) modalStatus: ModalStatusComponent
  products: Product[] = []
  productDepots: ProductDepot[] = []
  operations: ProductMove[] = []
  depots: Depot[] = []
  suppliers: Supplier[] = []
  producers: Producer[] = []
  vats: Vat[] = []
  note = ''
  totalItems = 0
  currentPage: number
  endPage: number
  optionsVisible = false
  selectedDepotFrom: number
  selectedDepotTo: number
  searchFulltext = ''
  dateOperation = ''
  initDateDUZP: string
  sortProp = 'id'
  sortDir = 'asc'
  loading = true
  isPrintingEANs = false
  uploadedFiles: File[] = []
  areFilesUploading = false
  keyEventSubscriptions: any = {}
  isClonedPmp = false

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private appRef: ApplicationRef,
    private notificationService: NotificationService,
    private translateService: TranslateService,
    private core: CoreService,
    public companyUtil: CompanyUtilService,
  ) {
    Events.listen('filters:clear', () => {
      this.clearOptions()
      this.clearList(true)
    })
  }

  ngOnInit() {
    this.refreshOptionsData().then(() => {
      this.loadViewSettings()
      this.fillDuplicatedPMP()
      if (this.selectedDepotFrom) this.search()
    })
    this.keyEventSubscriptions.scannedBarcode = this.core.services.keyEvent.onScannedBarcode.subscribe().then(code => {
      if (disableBarcodeScanner) {
        return
      }
      this.searchFulltext = code
      this.search().then((products: Product[]) => {
        if (products && products.length === 1) {
          setTimeout(() => {
            this.modalStockOperation.showProductOnChildModal(products[0])
            this.products = []
            this.searchFulltext = ''
          }, 100)
        }
        if (!products || (products && products.length < 1)) {
          setTimeout(() => {
            this.modalStockOperation.showProductOnChildModal(null, true, this.searchFulltext)
            this.products = []
            this.searchFulltext = ''
          }, 100)
        }
        setTimeout(() => this.appRef.tick(), 200)
        setTimeout(() => this.appRef.tick(), 500)
      })
    })
    this.loading = false
  }

  ngOnDestroy() {
    if (this.keyEventSubscriptions.scannedBarcode) {
      this.keyEventSubscriptions.scannedBarcode.unsubscribe()
      this.keyEventSubscriptions.scannedBarcode = null
    }
  }

  loadViewSettings(): void {
    const settings = this.core.services.storage.getSync(
      this.core.services.storage.keys.views.stockOperationListTransfer,
    )
    if (settings && Object.keys(settings).length > 0) {
      this.optionsVisible = settings.optionsVisible
      const doesSelectedDepotFromExists =
        this.depots.map(d => d.id).filter(d => d === Number(settings.selectedDepotFrom)).length > 0
      if (doesSelectedDepotFromExists && !!settings.selectedDepotFrom) {
        this.selectedDepotFrom = settings.selectedDepotFrom
      } else {
        this.selectedDepotFrom = this.depots.length < 2 ? this.depots[0].id : 0
      }
      const doesSelectedDepotToExists =
        this.depots.map(d => d.id).filter(d => d === Number(settings.selectedDepotTo)).length > 0
      if (doesSelectedDepotToExists && !!settings.selectedDepotTo) {
        this.selectedDepotTo = settings.selectedDepotTo
      } else {
        this.selectedDepotTo = this.depots.length < 2 ? this.depots[0].id : 0
      }
      this.operations = settings.operations.map(op => new ProductMove(op)) || []
      this.searchFulltext = settings.searchFulltext
      this.dateOperation = settings.dateOperation
      this.sortProp = settings.sortProp
      this.sortDir = settings.sortDir
      this.note = settings.note
      this.uploadedFiles =
        settings.uploadedFiles && settings.uploadedFiles.length > 0 ? settings.uploadedFiles.map(f => new File(f)) : []
    } else {
      this.selectedDepotTo = this.depots.length === 1 ? this.depots[0].id : 0
    }
  }

  saveViewSettings(): void {
    const settings = {
      optionsVisible: this.optionsVisible,
      selectedDepotFrom: this.selectedDepotFrom,
      selectedDepotTo: this.selectedDepotTo,
      operations: this.operations,
      dateOperation: this.dateOperation,
      searchFulltext: this.searchFulltext,
      sortProp: this.sortProp,
      sortDir: this.sortDir,
      note: this.note,
      uploadedFiles: this.uploadedFiles,
    }
    this.core.services.storage.set(this.core.services.storage.keys.views.stockOperationListTransfer, settings)
  }

  async refreshOptionsData(): Promise<any> {
    this.depots = this.core.lists.depots.filter(
      depot => depot.name !== 'all' && this.core.services.user?.user?.depots?.some(d => d.id === depot.id),
    )
    this.suppliers = this.core.lists.suppliers
    this.producers = this.core.lists.producers
    this.vats = this.core.lists.vats
  }

  clearOptions(type?: string): void {
    switch (type) {
      case 'depots':
        this.selectedDepotFrom = null
        this.selectedDepotTo = null
        break
      default:
        this.selectedDepotFrom = null
        this.selectedDepotTo = null
        this.dateOperation = null
        this.searchFulltext = ''
        this.products = []
        this.note = ''
        this.uploadedFiles = []
        break
    }
    this.saveViewSettings()
  }

  fillDuplicatedPMP() {
    const pmp = window['pmp-clone']
    if (pmp) {
      if (pmp.depotTo?.id > 0) {
        this.selectedDepotTo = pmp.depotTo.id
      }
      if (pmp.depotFrom?.id > 0) {
        this.selectedDepotFrom = pmp.depotFrom.id
      }
      this.operations = pmp.moves
      this.note = pmp.note || ''
      this.isClonedPmp = true
    }
  }

  clearList(isWithoutConfirmation = false): void {
    if (!isWithoutConfirmation && !confirm(this.translateService.instant('stock-transfer-page.delete.confirm'))) {
      return
    }
    this.operations = []
    this.saveViewSettings()
  }

  filterKeyDown(event): void {
    if (event.key === 'Enter') {
      this.search()
    }
  }

  async search(pageNo = 1, concat = false): Promise<any> {
    this.loading = true
    const filters: any = {
      listType: 'children_and_parents_without_children',
      fulltext: this.searchFulltext.trim(),
      enabled: true,
      isBundle: false,
      availability: 'stock',
    }
    if (this.selectedDepotFrom) {
      filters.depots = [Number(this.selectedDepotFrom)]
    }
    const receivedData = await this.core.services.product.getList(
      { filters: filters, page: pageNo, sort: 'name', direction: 'ASC' },
      SchemaUtil.product,
    )
    if (receivedData) {
      if (!concat) {
        this.products = receivedData.items
      } else {
        receivedData.items.forEach(prod => {
          this.products.push(new Product(prod))
        })
      }
      this.totalItems = receivedData.paginator ? receivedData.paginator.totalCount : 0
      this.endPage = receivedData.paginator ? receivedData.paginator.endPage : 0
      if (pageNo === 1) {
        this.currentPage = 1
        const el = document?.querySelector('.prod-list-scrolling')
        if (el && el.scrollTop !== undefined && el.scrollTop !== null) {
          el.scrollTop = 0
        }
      }
      setTimeout(() => (this.loading = false), 300)
      return this.products
    }
    setTimeout(() => (this.loading = false), 300)
  }

  depotFromSelected(event) {
    this.selectedDepotFrom = event
    this.search()
  }

  getProductDepotsWithQuantity(prod) {
    return prod.productDepots.filter(
      pDepot => Number(pDepot.depot.id) === Number(this.selectedDepotFrom) && pDepot?.quantityStock > 0,
    )
  }

  getDepotQuantity(pDepot): number {
    if (
      this.operations?.some(
        move =>
          Number(move.product.id) === Number(pDepot?.product?.id) &&
          move.batch === pDepot.batch &&
          move.supplier === pDepot.suppliers,
      )
    ) {
      const pushedMoveWithSamePDepot = this.operations.find(
        move =>
          Number(move.product.id) === Number(pDepot?.product?.id) &&
          move.batch === pDepot.batch &&
          move.supplier === pDepot.suppliers,
      )
      return pDepot.quantityStock - pushedMoveWithSamePDepot.amount
    } else {
      return pDepot.quantityStock
    }
  }

  sortBy(property: string): void {
    if (this.sortProp === property) {
      if (this.sortDir === 'asc') {
        this.sortDir = 'desc'
      } else {
        this.sortDir = 'asc'
      }
    } else {
      this.sortProp = property
    }
    this.saveViewSettings()
  }

  importBatchFromFile(operations: ProductMove[]): void {
    operations.forEach(op => this.addOperationToList(op))
    this.operations = [...this.operations]
    setTimeout(() => {
      this.appRef.tick()
    }, 50)
  }

  createBatchFromFile(res: any[]): void {
    if (res && res.length > 0) {
      const errorsInResponse = res.filter(r => Array.isArray(r) && typeof r[0] === 'string')
      if (errorsInResponse.length > 0) {
        this.notificationService.error(
          this.translateService.instant('stock-transfer-page.create.error', { error: errorsInResponse.join(',') }),
          null,
          true,
        )
        Sentry.captureMessage('list::createBatchFromFile errors: ' + res)
      } else {
        this.notificationService.success(this.translateService.instant('stock-transfer-page.create.success'))
        this.search()
      }
    }
  }

  operationListChange(moves: ProductMove[]) {
    this.operations = moves
    this.saveViewSettings()
    this.appRef.tick()
  }

  addOperationToList(operation: ProductMove): void {
    if (!operation || (operation && !operation.product)) {
      if (Array.isArray(operation) && operation.length > 0) {
        operation.forEach(err => {
          this.notificationService.error(
            this.translateService.instant('stock-transfer-page.save.error', {
              error: err && err.message ? err.message : err,
            }),
            null,
            true,
          )
        })
      } else {
        this.notificationService.error(this.translateService.instant('stock-transfer-page.save.error.500'), null, true)
      }
      return
    }

    if (operation.amount < 1) {
      alert(this.translateService.instant('stock-transfer-page.not-possible-to-store-in-minus'))
      return
    }

    operation = new ProductMove(operation)
    let exists = false
    this.operations.forEach((op: ProductMove, i: number) => {
      if (this.isProductInOperationsYet(op, operation)) {
        exists = true
        this.operations[i].amount = Number(op.amount) + Number(operation.amount)
        this.operations[i].purchasePriceSum = Number(op.purchasePriceSum) + Number(operation.purchasePriceSum)
        this.operations[i].supplier = operation.supplier
      }
    })
    if (!exists) {
      this.operations.push(operation)
    }
    this.saveViewSettings()
    this.appRef.tick()
  }

  updateOperation(operation: ProductMove): void {
    operation = new ProductMove(operation)
    this.operations.forEach((op: ProductMove, i: number) => {
      if (op.product.id === operation.product.id) {
        this.operations[i] = operation
        this.saveViewSettings()
        this.appRef.tick()
        return
      }
    })
  }

  removeOperation(operation: ProductMove): void {
    this.operations.forEach((op: ProductMove, i: number) => {
      if (op === operation) {
        this.operations.splice(i, 1)
        this.saveViewSettings()
        this.appRef.tick()
        return
      }
    })
  }

  isProductInOperationsYet(operation, selectedOperation): boolean {
    return (
      operation.product.id === selectedOperation.product.id &&
      operation.purchasePrice === selectedOperation.purchasePrice &&
      operation.sellPrice === selectedOperation.sellPrice &&
      operation.batch === selectedOperation.batch &&
      operation.expirationDate === selectedOperation.expirationDate &&
      operation.purchaseCurrency === selectedOperation.purchaseCurrency &&
      operation.purchaseCurrencyDate === selectedOperation.purchaseCurrencyDate &&
      operation.position1 === selectedOperation.position1 &&
      operation.position2 === selectedOperation.position2 &&
      operation.position3 === selectedOperation.position3
    )
  }

  updateNote(note: string): void {
    this.note = note
    this.saveViewSettings()
  }

  async createStockLoad(): Promise<void> {
    this.loading = true
    const movePack: ProductMovePack = new ProductMovePack({
      type: { id: 'transfer' },
      depotFrom: {
        id: this.selectedDepotFrom,
      },
      depotTo: {
        id: this.selectedDepotTo,
      },
      moves: this.operations,
    })
    if (this.note && this.note !== 'null') {
      movePack.note = this.note
    }
    // check if depots exists for sure in this company (prevents multiple company in multiple tabs chaos)
    let areDepotsOk = true
    movePack.moves.forEach(mo => {
      const doesSelectedDepotsOnMoveExists =
        this.depots.map(d => d.id).filter(d => d === Number(mo.depotFrom) || d === Number(mo.depotTo)).length > 0
      if (!doesSelectedDepotsOnMoveExists) {
        areDepotsOk = false
        return
      }
    })
    if (!areDepotsOk) {
      alert(this.translateService.instant('stock-transfer-page.cannot-be-stocked'))
      return
    }
    this.core.services.productMovePack
      .create(movePack)
      .then(result => {
        if (result && result.id) {
          this.notificationService.success(this.translateService.instant(`alert.transfer.stock`))
          this.loading = true
          window['selectedSupplier'] = undefined
          this.updateUploadedFilesWithProductMovePackId(result.id)
            .then(res => {
              this.loading = false
              this.clearOptions()
              this.clearList(true)
              this.modalStatus.showChildModal(
                this.translateService.instant('info'),
                this.translateService.instant('stock-in-page.success'),
                true,
              )
            })
            .catch(e => `createStockLoad err, on fileUpdate: ${e.message}`)
        } else {
          this.modalStatus.showChildModal(
            this.translateService.instant('warning'),
            this.translateService.instant('stock-transfer-page.processing-error'),
            true,
          )
        }
        this.loading = false
        this.loadViewSettings()
      })
      .catch(error => {
        this.loading = false
        alert(
          this.translateService.instant('stock-transfer-page.error') +
            (error && error.message ? error.message : typeof error === 'string' ? error : ''),
        )
        console.log(error)
      })
  }

  printingSelect(type: string): void {
    let ops: any[] = []
    switch (type) {
      case 'all':
        ops = this.operations.map(op => {
          op.isEANPrintable = true
          return op
        })
        break
      case 'none':
        ops = this.operations.map(op => {
          op.isEANPrintable = false
          return op
        })
        break
      case 'invert':
        ops = this.operations.map(op => {
          op.isEANPrintable = !op.isEANPrintable
          return op
        })
        break
    }
    this.operations = ops
  }

  refreshOperationsHack(): void {
    // we need just to initiate pipe reload
    this.operations = [...this.operations]
  }

  reloadViewAfterProductUpdate(product: Product): void {
    if (product && product.id > 0) {
      this.search().then(() => {
        this.operations.forEach((op: ProductMove, i: number) => {
          if (op.product.id === product.id) {
            this.operations[i].product = product
            this.updateOperation(this.operations[i])
          }
        })
        this.refreshOperationsHack()
        this.notificationService.success(this.translateService.instant('stock-transfer-page.edit.success'))
      })
    } else {
      if (Array.isArray(product) && product.length > 0) {
        product.forEach(err => {
          this.notificationService.error(
            this.translateService.instant('stock-transfer-page.edit.error', {
              error: err && err.message ? err.message : err,
            }),
            null,
            true,
          )
        })
      } else {
        this.notificationService.error(this.translateService.instant('stock-transfer-page.edit.error.500'), null, true)
      }
    }
  }

  uploadFiles(inputEvent: any): Promise<Array<File> | any> {
    this.areFilesUploading = true
    return new Promise((resolve, reject) => {
      if (inputEvent.target.files && inputEvent.target.files.length > 0) {
        const files = Array.from(inputEvent.target.files)
        const promisesBase64s = files.map(f => this.processUploadedFileToBase64(f))
        Promise.all(promisesBase64s)
          .then(resolvedBase64s => {
            const promisesUploads = resolvedBase64s.map(b => {
              return this.core.services.file.create(new File(b))
            })
            return Promise.all(promisesUploads).then(res => {
              this.uploadedFiles.push(...res)
              this.notificationService.success(this.translateService.instant('alert.file.uploaded'))
              let i = 0
              for (const r of res) {
                this.uploadedFiles[i]['filename'] = files[i]['name']
                i++
              }
              this.saveViewSettings()
              return res
            })
          })
          .catch(e => {
            console.log(e)
          })
          .then((resolvedUploadedFiles: File[] | void) => {
            this.areFilesUploading = false
            resolve(resolvedUploadedFiles)
          })
          .catch(e => console.log(e))
      } else {
        this.areFilesUploading = false
        reject(this.translateService.instant('no-file'))
      }
    })
  }

  processUploadedFileToBase64(file: any): Promise<string | any> {
    return new Promise((resolve, reject) => {
      if (file) {
        const reader = new FileReader()
        reader.readAsBinaryString(new Blob([file]))
        reader.onload = evt => {
          resolve({
            text: file.name,
            originalFilename: file.name,
            mimeType: file.type,
            base64Data: `data:application/octet-stream;base64,${btoa(String(evt.target['result']))}`,
          })
        }
        reader.onerror = () => {
          reject(this.translateService.instant('stock-transfer-page.file-processing-error'))
        }
      } else {
        reject(this.translateService.instant('no-file'))
      }
    })
  }

  updateUploadedFilesWithProductMovePackId(pmpId: number): Promise<Array<any>> {
    return new Promise(resolve => {
      if (!this.uploadedFiles || (this.uploadedFiles && this.uploadedFiles.length === 0)) {
        return resolve([])
      }
      const promises = this.uploadedFiles.map(f => this.core.services.file.update(f, null, null, null, null, pmpId))
      Promise.all(promises).then(res => {
        resolve(res)
      })
    })
  }

  removeUploadedFile(file: File) {
    if (file && file.id > 0) {
      this.uploadedFiles.forEach((f, i) => {
        if (file.id === f.id) {
          try {
            this.uploadedFiles.splice(i, 1)
            this.notificationService.success(this.translateService.instant('alert.file.removed'))
          } catch (err) {
            console.log(err)
          }
        }
      })
      this.saveViewSettings()
    }
  }

  printList(): void {
    const htmlRows = this.operations
      .map(
        op => `
      <tr>
        <td>${op.product ? op.product.fullName : ''}</td>
        <td class="text-right">${op.product ? op.product.ean : ''}</td>
        <td class="text-right">${op.product ? op.product.code : ''}</td>
        <td class="text-right">${op.amount}</td>
        <td class="text-right">${op.purchasePrice !== 0 ? Number(op.purchasePrice).toFixed(2) : '0.00'}</td>
      </tr>`,
      )
      .join('')
    const html = `
      <table>
        <thead>
          <tr>
            <th>Název</th>
            <th>EAN</th>
            <th>Kód</th>
            <th>Počet</th>
            <th>Nákupní cena</th>
          </tr>
        </thead>
        <tbody>${htmlRows}</tbody>
      </table>`
    const pWidth = 900
    const pHeight = 720
    const popupWin = window.open(
      '',
      'Seznam položek k naskladnění',
      `toolbar=no,location=no,directories=no,status=no,menubar=no,scrollbars=yes,resizable=yes,width=${pWidth},height=${pHeight},top=${
        screen.height - (screen.height + pHeight) / 2
      },left=${screen.width - (screen.width + pWidth) / 2}`,
    )
    popupWin.document.body.innerHTML = html
    const styles = document.createElement('style')
    styles.innerHTML = `
      table {width: 100%; border: none; border-collapse: collapse;}
      td, th {border: 1px solid #333; padding: 1mm;}
      .text-right {text-align: right;}`
    popupWin.document.head.appendChild(styles)
    setTimeout(() => {
      popupWin.print()
    }, 300)

    setTimeout(() => {
      popupWin.close()
    }, 500)
  }

  getThumbUrl(image: File, format: 'w135wp' | 'w2048wp' = 'w135wp'): string {
    return imageGetThumbUrl(image, format)
  }
}
